// The one place the client decides what a role is.
//
// Before this existed, five different functions each guessed differently at what an unfamiliar role
// string meant -- and every one of them guessed generously, so a typo or a blank field resolved to
// staff. The rule now is that a role is one of these seven values or it is `denied`; there is no
// third outcome and nothing infers a role from anything else.
//
// Kept in step with SYSTEM_ROLES / normaliseRole in functions/index.js. Changing one means changing
// the other, and the security rules alongside them.

export const ROLE = {
  DEVELOPER: 'developer',
  SYSTEM_ADMIN: 'system_admin',
  TEACHER: 'teacher',
  NON_TEACHING: 'non_teaching',
  STUDENT: 'student',
  PARENT: 'parent',
  DENIED: 'denied',
};

export const KNOWN_ROLES = Object.values(ROLE);

// What a school administrator may assign. `developer` is absent on purpose: it lives in the root
// `developers` collection, because a school admin can write user_permissions and any role assignable
// there is a role they could grant themselves.
export const ASSIGNABLE_ROLES = [
  ROLE.SYSTEM_ADMIN,
  ROLE.TEACHER,
  ROLE.NON_TEACHING,
  ROLE.STUDENT,
  ROLE.PARENT,
  ROLE.DENIED,
];

export const ROLE_LABEL = {
  [ROLE.DEVELOPER]: 'Developer',
  [ROLE.SYSTEM_ADMIN]: 'System Admin',
  [ROLE.TEACHER]: 'Teacher',
  [ROLE.NON_TEACHING]: 'Non-teaching Staff',
  [ROLE.STUDENT]: 'Student',
  [ROLE.PARENT]: 'Parent',
  [ROLE.DENIED]: 'No access',
};

// Every spelling that has ever been written to a Role or role field, mapped to canonical. Casing was
// inconsistent from the start ('admin' lowercase but 'Teacher' capitalised), and CSV import wrote
// whatever title the school typed -- so this is a translation table for real data, not a nicety.
const LEGACY = {
  admin: ROLE.SYSTEM_ADMIN,
  'system admin': ROLE.SYSTEM_ADMIN,
  systemadmin: ROLE.SYSTEM_ADMIN,
  sysadmin: ROLE.SYSTEM_ADMIN,
  teacher: ROLE.TEACHER,
  tch: ROLE.TEACHER,
  staff: ROLE.NON_TEACHING,
  'non-teaching': ROLE.NON_TEACHING,
  'non teaching': ROLE.NON_TEACHING,
  nonteaching: ROLE.NON_TEACHING,
  student: ROLE.STUDENT,
  user: ROLE.STUDENT,
  parent: ROLE.PARENT,
  guardian: ROLE.PARENT,
  developer: ROLE.DEVELOPER,
};

/**
 * Resolve any stored role string to a canonical role.
 *
 * Anything unrecognised becomes `denied` rather than a guess. That is the whole point: an account
 * whose role we cannot read is not a lesser account, it is an account nothing can reason about, and
 * the safe reading of "I don't know who this is" has never been "probably a teacher".
 */
export const normaliseRole = (raw) => {
  const r = String(raw || '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (!r) return ROLE.DENIED;
  if (KNOWN_ROLES.includes(r)) return r;
  return LEGACY[r] || ROLE.DENIED;
};

export const isStaffRole = (role) =>
  role === ROLE.SYSTEM_ADMIN || role === ROLE.TEACHER || role === ROLE.NON_TEACHING;

export const isAdminRole = (role) =>
  role === ROLE.SYSTEM_ADMIN || role === ROLE.DEVELOPER;

// module_access stores its columns as teacherAccess / staffAccess / studentAccess, and those keys
// live in customer data. Rather than migrate that too, canonical roles map onto the existing keys.
export const ACCESS_KEY_FOR = {
  [ROLE.TEACHER]: 'teacher',
  [ROLE.NON_TEACHING]: 'staff',
  [ROLE.STUDENT]: 'student',
  [ROLE.PARENT]: 'parent',
};

// The permission checkboxes in Attendance / Invigilation / Patrol / Quiz store these labels in
// customer data too, so canonical roles map onto them rather than the other way round. Roles absent
// from this map match no permission list, which is exactly what should happen for parent and denied.
export const ACTOR_BUCKET_FOR = {
  [ROLE.DEVELOPER]: 'Admin',
  [ROLE.SYSTEM_ADMIN]: 'Admin',
  [ROLE.TEACHER]: 'Teacher',
  [ROLE.NON_TEACHING]: 'Staff',
  [ROLE.STUDENT]: 'Student',
};


// --- SID <-> account translation --------------------------------------------
//
// Assessment data arrives from the government system keyed by SID and is never rewritten, so the
// translation cannot live on the record. It lives in the ID token instead: syncAccountClaims stamps
// a student's own `sid` and a guardian's `childSids`, and the security rule compares
// `resource.data.SID` against those directly.
//
// The consequence for callers: a student or guardian listing SID-keyed data MUST filter on the same
// field, or Firestore refuses the whole query rather than returning less.
//
//   const { sid, childSids } = await getSubjectClaims();
//   query(collection(db, 'schools', id, 'ass_raw_data'), where('SID', '==', sid))
//
// The reverse direction -- email or SID to a person's particulars for display -- is a profile
// lookup, not a claim: student_profiles is keyed by email and carries sid, class and name.
export const getSubjectClaims = async (user) => {
  if (!user) return { sid: '', childSids: [] };
  try {
    // Not forced: a forced refresh on every read would be a network round trip per query. Claims
    // land within the hour, or immediately on next sign-in.
    const token = await user.getIdTokenResult();
    return {
      sid: token.claims.sid || '',
      childSids: Array.isArray(token.claims.childSids) ? token.claims.childSids : [],
    };
  } catch (e) {
    console.error('Could not read subject claims', e);
    return { sid: '', childSids: [] };
  }
};
